import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { FEATURES, FeatureType } from '../../config/features';
import './PremiumUpgradeModal.css';

interface PremiumUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureId: FeatureType;
  onUpgrade: () => void;
  /** Опциональные переопределения (для совместимости с mobile API) */
  title?: string;
  description?: string;
  customMessage?: string;
  onNavigateToSubscription?: () => void;
}

/** Маппинг иконок benefits (mobile → web Icon names) */
const BENEFIT_ICONS: Record<string, string> = {
  brain: 'lightbulb',
  'lightbulb-on': 'lightbulb',
  'chart-line': 'chart-bar',
  robot: 'message-circle',
  camera: 'camera',
  'clock-fast': 'clock',
  'check-circle': 'check-circle',
  bank: 'building',
  sync: 'sync',
  'shield-lock': 'shield',
  'star-four-points': 'star',
  'rocket-launch': 'zap',
  'diamond-stone': 'star',
  headphones: 'phone',
  'chart-timeline-variant': 'chart-bar',
  'bell-ring': 'bell',
  target: 'target',
  'piggy-bank': 'wallet',
  'credit-card-search': 'search',
  alarm: 'clock',
  'bank-transfer': 'repeat',
  infinity: 'refresh',
  'star-circle': 'star',
};

/**
 * Модальное окно с предложением апгрейда до Premium
 * Соответствует mobile PremiumUpgradeModal по структуре и i18n
 */
export const PremiumUpgradeModal: React.FC<PremiumUpgradeModalProps> = ({
  isOpen,
  onClose,
  featureId,
  onUpgrade,
  title: titleOverride,
  description: descriptionOverride,
  customMessage,
  onNavigateToSubscription,
}) => {
  const { t } = useTranslation();
  const feature = FEATURES[featureId];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, [isOpen]);

  const getEnhancedDescription = (): string => {
    if (customMessage) return customMessage;
    if (descriptionOverride) return descriptionOverride;
    if (featureId === 'ai_chat') return t('premiumModal.aiChatDescription');
    if (featureId === 'recurring_transactions') return t('premiumModal.recurringDescription');
    return feature?.description || t('premiumModal.defaultDescription');
  };

  const finalTitle = titleOverride || feature?.name || t('premiumModal.premiumFeature');
  const finalDescription = getEnhancedDescription();

  const getFeatureSpecificBenefits = (): Array<{ icon: string; text: string }> => {
    switch (featureId) {
      case 'ai_chat':
        return [
          { icon: 'lightbulb', text: t('premiumModal.benefits.aiAssistant') },
          { icon: 'lightbulb', text: t('premiumModal.benefits.personalizedInsights') },
          { icon: 'chart-bar', text: t('premiumModal.benefits.autoAnalysis') },
          { icon: 'message-circle', text: t('premiumModal.benefits.instantAnswers') },
        ];
      case 'voice_input':
        return [
          { icon: 'mic', text: t('premiumModal.benefits.autoFill') },
          { icon: 'check-circle', text: t('premiumModal.benefits.saveTimeInput') },
          { icon: 'clock', text: t('premiumModal.benefits.aiAccuracy') },
        ];
      case 'receipt_scanner':
        return [
          { icon: 'camera', text: t('premiumModal.benefits.instantRecognition') },
          { icon: 'check-circle', text: t('premiumModal.benefits.autoFill') },
          { icon: 'clock', text: t('premiumModal.benefits.saveTimeInput') },
          { icon: 'check-circle', text: t('premiumModal.benefits.aiAccuracy') },
        ];
      case 'analytics':
        return [
          { icon: 'chart-bar', text: t('premiumModal.benefits.detailedAnalytics') },
          { icon: 'chart-bar', text: t('premiumModal.benefits.forecasting') },
          { icon: 'lightbulb', text: t('premiumModal.benefits.aiInsights') },
        ];
      case 'recurring_transactions':
        return [
          { icon: 'bell', text: t('premiumModal.benefits.neverForget') },
          { icon: 'search', text: t('premiumModal.benefits.trackSubscriptions') },
          { icon: 'clock', text: t('premiumModal.benefits.smartReminders') },
        ];
      default:
        return [];
    }
  };

  const generalBenefits = [
    { icon: 'lightbulb', text: t('premiumModal.benefits.proAnalysis') },
    { icon: 'star', text: t('premiumModal.benefits.allFeatures') },
    { icon: 'refresh', text: t('premiumModal.benefits.unlimited') },
    { icon: 'building', text: t('premiumModal.benefits.bankHistory') },
    { icon: 'message-circle', text: t('premiumModal.benefits.aiChat') },
    { icon: 'shield', text: t('premiumModal.benefits.maxProtection') },
  ];

  const featureBenefits = getFeatureSpecificBenefits();
  const allBenefits = featureBenefits.length > 0 ? featureBenefits : generalBenefits.slice(0, 6);

  const handleUpgrade = () => {
    onClose();
    if (onNavigateToSubscription) {
      onNavigateToSubscription();
    }
    onUpgrade();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="premium-upgrade-modal-overlay" onClick={onClose}>
      <div className="premium-upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="premium-upgrade-modal__close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          type="button"
          aria-label={t('common.close')}
        >
          ×
        </button>

        <div className="premium-upgrade-modal__icon">
          <Icon name="lock" size={64} color="#FF8C00" />
        </div>

        <h2 className="premium-upgrade-modal__title">{finalTitle}</h2>
        <p className="premium-upgrade-modal__description">{finalDescription}</p>

        <div className="premium-upgrade-modal__motivation">
          <Icon name="zap" size={24} color="#FF8C00" />
          <span>{t('premiumModal.motivationText')}</span>
        </div>

        <div className="premium-upgrade-modal__badge">
          <Icon name="star" size={20} color="#FFFFFF" />
          <span>{t('premiumModal.premiumFeature')}</span>
        </div>

        <div className="premium-upgrade-modal__benefits">
          <h3>
            {featureBenefits.length > 0
              ? t('premiumModal.featureBenefitsTitle')
              : t('premiumModal.premiumBenefitsTitle')}
          </h3>
          {allBenefits.map((benefit, index) => (
            <div key={index} className="premium-upgrade-modal__benefit">
              <Icon
                name={BENEFIT_ICONS[benefit.icon] || benefit.icon}
                size={20}
                color="var(--color-primary, #FF8C00)"
              />
              <span>{benefit.text}</span>
            </div>
          ))}
        </div>

        <div className="premium-upgrade-modal__cta">
          <Icon name="star" size={20} color="#34C759" />
          <span>{t('premiumModal.ctaText')}</span>
        </div>

        <div className="premium-upgrade-modal__actions">
          <button
            className="premium-upgrade-modal__button premium-upgrade-modal__button--primary"
            onClick={handleUpgrade}
          >
            <Icon name="star" size={20} color="#FFFFFF" />
            <span>{t('premiumModal.getPremiumNow')}</span>
          </button>
          <button
            className="premium-upgrade-modal__button premium-upgrade-modal__button--secondary"
            onClick={onClose}
          >
            {t('premiumModal.maybeLater')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
